import React, { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function AccountEquityCurve({ equityCurve = [], balance = 0 }) {
  const [unit, setUnit] = useState('$');     // $ | %
  const [period, setPeriod] = useState('ALL');

  // Filter by period
  const filteredData = useMemo(() => {
    if (!equityCurve || equityCurve.length === 0) return [];
    if (period === 'ALL') return equityCurve;

    const now = new Date();
    let cutoffDays = 30;
    switch (period) {
      case '1D': cutoffDays = 1; break;
      case '1W': cutoffDays = 7; break;
      case '1M': cutoffDays = 30; break;
      case '3M': cutoffDays = 90; break;
      case '6M': cutoffDays = 180; break;
      case '1Y': cutoffDays = 365; break;
      default: return equityCurve;
    }
    const cutoff = new Date(now.getTime() - cutoffDays * 86400000);
    return equityCurve.filter(d => d.date && new Date(d.date) >= cutoff);
  }, [equityCurve, period]);

  // For % mode, calculate percentage of balance
  const chartData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    if (unit === '%') {
      // Calculate starting balance = current balance - total cumulative profit
      const lastEquity = filteredData[filteredData.length - 1]?.equity || 0;
      const startingBalance = balance > 0 ? (balance - lastEquity) : 0;
      const base = startingBalance > 0 ? startingBalance : balance > 0 ? balance : 1;

      return filteredData.map(d => ({
        ...d,
        value: (d.equity / base) * 100,
      }));
    }
    return filteredData.map(d => ({ ...d, value: d.equity }));
  }, [filteredData, unit, balance]);

  const dataKey = 'value';
  const yFormatter = unit === '%'
    ? (v) => `${v.toFixed(1)}%`
    : (v) => v === 0 ? '$0' : `$${(v/1000).toFixed(0)}K`;

  return (
    <div>
      {/* Controls - top right */}
      <div className="flex items-center justify-end gap-2 mb-3">
        {/* $ / % toggle */}
        <div className="flex bg-[#21262d] rounded-md overflow-hidden">
          <button
            onClick={() => setUnit('$')}
            className={`px-2.5 py-1 text-[10px] font-bold transition ${unit === '$' ? 'bg-[#00ff88] text-[#0d1117]' : 'text-[#8b949e] hover:text-[#e6edf3]'}`}
          >$</button>
          <button
            onClick={() => setUnit('%')}
            className={`px-2.5 py-1 text-[10px] font-bold transition ${unit === '%' ? 'bg-[#00ff88] text-[#0d1117]' : 'text-[#8b949e] hover:text-[#e6edf3]'}`}
          >%</button>
        </div>

        {/* Period buttons */}
        <div className="flex bg-[#21262d] rounded-md overflow-hidden">
          {['1W','1M','3M','6M','1Y','ALL'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-2 py-1 text-[10px] font-medium transition ${period === p ? 'bg-[#00ff88] text-[#0d1117] font-bold' : 'text-[#8b949e] hover:text-[#e6edf3]'}`}
            >{p}</button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="eqCurveGradGreen" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00ff88" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#00ff88" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="eqCurveGradRed" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff4757" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#ff4757" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
            <XAxis dataKey="date" stroke="#8b949e" tick={{ fill: '#8b949e', fontSize: 9 }} />
            <YAxis stroke="#8b949e" tick={{ fill: '#8b949e', fontSize: 9 }} tickFormatter={yFormatter} />
            <Tooltip
              contentStyle={{ backgroundColor: '#161b22', border: '1px solid #21262d', borderRadius: '8px' }}
              formatter={(v) => [unit === '%' ? `${Number(v).toFixed(2)}%` : `$${Number(v).toLocaleString(undefined, {minimumFractionDigits: 2})}`, unit === '%' ? 'Change' : 'Equity']}
              labelFormatter={(l) => l}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={chartData[chartData.length - 1]?.value >= 0 ? '#00ff88' : '#ff4757'}
              fill={chartData[chartData.length - 1]?.value >= 0 ? 'url(#eqCurveGradGreen)' : 'url(#eqCurveGradRed)'}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[260px] flex items-center justify-center">
          <p className="text-[#484f58] text-sm">No equity data available</p>
        </div>
      )}
    </div>
  );
}
